/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@material-ui/core'

import { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { MetricCard } from '../../components/MetricCard'
import { getAverageFrequency, FrequencyStats, AverageFrequencyParams } from './api'
import { montserratStyle } from '../../styles'

interface AverageFrequencyProps {
  period1: { startDate: Moment; endDate: Moment }
  period2: { startDate: Moment; endDate: Moment }
  compara: boolean
  trigger: number
  storeIds?: number[]
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
  // eslint-disable-next-line react/require-default-props
  extraCard?: React.ReactNode
}

export const AverageFrequency = ({
  period1,
  period2,
  compara,
  trigger,
  storeIds,
  onLoaded,
  extraCard,
}: AverageFrequencyProps) => {
  const [data, setData] = useState<FrequencyStats | null>(null)
  const [data2, setData2] = useState<FrequencyStats | null>(null)
  const [statsCall, statsLoading] = useApiCall<AverageFrequencyParams, { data: FrequencyStats }>(
    getAverageFrequency,
  )

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'AverageFrequency' })

  const fetchData = async () => {
    try {
      // Periodo 1 - usar fechas completas
      const startDate1 = period1.startDate.format('YYYY-MM-DD')
      const endDate1 = period1.endDate.format('YYYY-MM-DD')

      const response1 = await statsCall({
        startDate: startDate1,
        endDate: endDate1,
        ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
      })

      if (response1?.data) {
        setData(response1.data)
      }

      if (compara) {
        // Periodo 2 - usar fechas completas
        const startDate2 = period2.startDate.format('YYYY-MM-DD')
        const endDate2 = period2.endDate.format('YYYY-MM-DD')

        const response2 = await statsCall({
          startDate: startDate2,
          endDate: endDate2,
          ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
        })

        if (response2?.data) {
          setData2(response2.data)
        }
      } else {
        setData2(null)
      }
    } catch (error) {
      console.error('Error fetching frequency statistics:', error)
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const calculateDiff = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((current - previous) / previous) * 100
  }

  const formatStats = (current?: FrequencyStats | null, previous?: FrequencyStats | null) => {
    if (!previous) {
      return {
        general: {
          value: current?.general.value || 0,
          prev: 0,
          diff: 0,
          isPositive: true,
        },
        recurrent: {
          value: current?.recurrent.value || 0,
          prev: 0,
          diff: 0,
          isPositive: true,
        },
        byLevel:
          current?.byLevel.map(level => ({
            ...level,
            prev: 0,
            diff: 0,
            isPositive: true,
          })) || [],
      }
    }

    return {
      general: {
        value: current?.general.value || 0,
        prev: previous.general.value,
        diff: calculateDiff(current?.general.value || 0, previous.general.value),
        isPositive: (current?.general.value || 0) >= previous.general.value,
      },
      recurrent: {
        value: current?.recurrent.value || 0,
        prev: previous.recurrent.value,
        diff: calculateDiff(current?.recurrent.value || 0, previous.recurrent.value),
        isPositive: (current?.recurrent.value || 0) >= previous.recurrent.value,
      },
      byLevel:
        current?.byLevel.map((level, index) => ({
          ...level,
          prev: previous.byLevel[index]?.value || 0,
          diff: calculateDiff(level.value, previous.byLevel[index]?.value || 0),
          isPositive: level.value >= (previous.byLevel[index]?.value || 0),
        })) || [],
    }
  }

  const displayData = formatStats(data, data2 || undefined)

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className={`grid grid-cols-1 ${extraCard ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
        <MetricCard
          title="Frecuencia promedio (todos los usuarios)"
          value={displayData.general.value}
          prev={displayData.general.prev}
          diff={displayData.general.diff}
          isPositive={displayData.general.isPositive}
          showComparison={compara}
          comparisonMonth={period2.startDate.format('DD/MM')}
          comparisonYear={period2.endDate.format('DD/MM/YYYY')}
          isLoading={statsLoading}
        />
        <MetricCard
          title="Frecuencia promedio de usuarios recurrentes"
          value={displayData.recurrent.value}
          prev={displayData.recurrent.prev}
          diff={displayData.recurrent.diff}
          isPositive={displayData.recurrent.isPositive}
          showComparison={compara}
          comparisonMonth={period2.startDate.format('DD/MM')}
          comparisonYear={period2.endDate.format('DD/MM/YYYY')}
          isLoading={statsLoading}
        />
        {extraCard}
      </div>
      <span
        className="text-base font-bold mt-2 mb-1"
        style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
      >
        Frecuencia promedio de usuarios recurrentes
      </span>
      {!statsLoading && !data && (
        <div className="flex items-center justify-center h-32">
          <span className="text-gray-600" style={montserratStyle}>
            No hay datos disponibles para mostrar en este momento.
          </span>
        </div>
      )}
      {!statsLoading ? (
        // Hasta 4 cards por fila para niveles recurrentes, sin scroll horizontal
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayData.byLevel.map(n => (
            <MetricCard
              key={n.nivel}
              title={n.nivel}
              value={n.value}
              prev={n.prev}
              diff={n.diff}
              isPositive={n.isPositive}
              showComparison={compara}
              comparisonMonth={period2.startDate.format('DD/MM')}
              comparisonYear={period2.endDate.format('DD/MM/YYYY')}
              className="min-w-[100px]"
              isLoading={statsLoading}
            />
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center h-32">
          <CircularProgress size={40} />
        </div>
      )}
    </div>
  )
}

AverageFrequency.defaultProps = {
  storeIds: undefined,
}
